import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Badge } from 'react-bootstrap'
import ReactPlaceholder from 'react-placeholder/lib'
import { useHistory } from 'react-router-dom'
import { useGet, useSaveUpdate } from '../../../hooks/process-data'
import { appUrl, doToast } from '../../../utils'
import { FormCustom } from '../../../components/form'
import SweetAlert2 from 'react-sweetalert2'
import { useSelector } from 'react-redux'
import LoadingIcon from '../../../components/loading/LoadingIcon'

export default function ZoneBoxForm({ idEventZone }) {
  const history = useHistory()
  const [swalProps, setSwalProps] = useState({ show: false })
  const [getRow, setGetRow] = useState(null)
  const [ready, setReady] = useState(false)

  const { id_customers_reservation_event_zone } = useSelector(
    state => state.sessioncustomer
  )

  const { rows, isProcessing, error, setReload } = useGet({
    url: `/event/zone/${idEventZone}/get/public`,
    rowId: idEventZone
  })

  // Selección
  const [selected, setSelected] = useState([]) // [{id, code}]

  const isSoldOut = useMemo(
    () => !!getRow && parseInt(getRow.sold_out, 10) === 1,
    [getRow]
  )

  const hasDetails = useMemo(
    () => !!getRow?.event_zone_detail?.length,
    [getRow]
  )

  const zoneTypeLabel = useMemo(() => {
    const t = parseInt(getRow?.id_type_event_zone || 0, 10)
    return t === 3 ? 'box' : 'asiento'
  }, [getRow])

  // Ajusta aquí según tus estados reales
  const getSeatState = det => {
    const status = (det.status_event_zone || '').toUpperCase()

    // Ejemplos típicos:
    if (status === 'PENDING')
      return 'available'

    return 'unavailable'
  }

  const seatCode = det => `${det.column_event_zone}${det.row_event_zone}`

  const isSelected = det =>
    selected.some(
      s => parseInt(s.id, 10) === parseInt(det.id_event_zone_detail, 10)
    )

  const toggleSeat = det => {
    const state = getSeatState(det)
    if (state !== 'available') return

    const id = det.id_event_zone_detail
    const code = seatCode(det)

    if (isSelected(det)) {
      setSelected(prev =>
        prev.filter(s => parseInt(s.id, 10) !== parseInt(id, 10))
      )
    } else {
      setSelected(prev => [...prev, { id, code }])
    }
  }

  // Construir matriz
  const { columns, rowsIndex, matrix } = useMemo(() => {
    const details = getRow?.event_zone_detail || []
    if (!details.length) return { columns: [], rowsIndex: [], matrix: {} }
  
    // Columnas VISUALES = números
    const colSet = new Set(details.map(d => Number(d.row_event_zone)))
  
    // Filas VISUALES = letras
    const rowSet = new Set(details.map(d => d.column_event_zone))
  
    const columnsSorted = Array.from(colSet).sort((a, b) => a - b)
    const rowsSorted = Array.from(rowSet).sort((a, b) => a.localeCompare(b))
  
    const m = {}
    for (const r of rowsSorted) m[r] = {}
  
    for (const d of details) {
      const visualRow = d.column_event_zone   // A, B, C
      const visualCol = Number(d.row_event_zone) // 1,2,3
  
      m[visualRow][visualCol] = d
    }
  
    return {
      columns: columnsSorted,   // 1..N (horizontal)
      rowsIndex: rowsSorted,    // A..Z (vertical)
      matrix: m
    }
  }, [getRow])

  // Submit
  const dataSubmit = useMemo(
    () => ({
      p_id_event_zone: idEventZone,
      p_list_event_zone_detail: selected.map(s => s.id).toString(),
      p_id_customers_reservation_event_zone: id_customers_reservation_event_zone
    }),
    [selected, idEventZone, id_customers_reservation_event_zone]
  )

  const { doSubmit, setDoSubmit } = useSaveUpdate({
    data: dataSubmit,
    url: '/event/zone/insert/detail',
    setReload: _ => {
      setReload(true)
      setReady(false)
      setSelected([])

      setSwalProps({
        icon: 'success',
        title: 'Entradas reservadas',
        show: true,
        confirmButtonText: 'Ir al carrito',
        onConfirm: _ => {
          history.push(appUrl({ url: '/carrito' }))
        },
        didClose: _ => setSwalProps({ show: false })
      })
    }
  })

  useEffect(() => {
    if (error) {
      history.push(appUrl({ url: '/' }))
      return
    }
    if (!isProcessing) {
      if (!rows) {
        history.push(appUrl({ url: '/' }))
      } else {
        setGetRow(rows)
        setReady(true)
      }
    }
  }, [rows, error, isProcessing, history])

  const total = useMemo(() => {
    const unit = Number(getRow?.price_event_zone || 0)
    return (unit * selected.length).toFixed(2)
  }, [getRow, selected.length])

  const canConfirm = selected.length > 0 && !isSoldOut && hasDetails

  return (
    <FormCustom
      viewSubmit={false}
      onSubmit={_ => {
        if (!hasDetails || isSoldOut) return
        if (selected.length === 0) {
          doToast({
            response: 'warning',
            message: `Selecciona al menos un ${zoneTypeLabel} antes de continuar.`,
            toast
          })
          return
        }
        setDoSubmit(true)
      }}
    >
      <ReactPlaceholder ready={ready} showLoadingAnimation rows={10}>
        {/* HEADER COMPACTO */}
        {getRow && (
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div className="fw-semibold">{getRow.name_event_zone}</div>
              <div className="text-muted small">
                {getRow.name_event} · {getRow.name_type_event_zone} ·{' '}
                <strong>
                  {getRow.name_money} {getRow.price_event_zone}
                </strong>
              </div>
            </div>

            <div className="text-end">
              <div className="text-muted small">Seleccionados</div>
              <Badge bg={selected.length ? 'primary' : 'secondary'}>
                {selected.length}
              </Badge>
            </div>
          </div>
        )}

        {/* SOLD OUT */}
        {(!hasDetails || isSoldOut) && (
          <div className="alert alert-danger mb-0">
            <i className="fas fa-exclamation-triangle me-2" />
            <span>Entradas agotadas para esta zona.</span>
          </div>
        )}

        {/* CONTENIDO (3 filas) */}
        {hasDetails && !isSoldOut && (
          <div className="zoneModalLayout">
            {/* FILA 1: MAPA */}
            <div className="zoneModalRow zoneModalRow--map">
              <div className="zoneMapCard">
                <div className="zoneMapHeader">
                  {/* <span className="text-muted small">Escenario ↑</span> */}
                </div>

                <div className="seatmap-scroll">
                  <div className="seatmap-grid">
                    {/* Header columnas */}
                    <div className="seatmap-row seatmap-header">
                      <div className="seatmap-cell seatmap-corner" />
                      {columns.map(col => (
                        <div key={col} className="seatmap-cell seatmap-colhead">
                          {col}
                        </div>
                      ))}
                    </div>

                    {/* Filas */}
                    {rowsIndex.map(r => (
                      <div key={r} className="seatmap-row">
                        <div className="seatmap-cell seatmap-rowhead">{r}</div>

                        {columns.map(col => {
                          const det = matrix?.[r]?.[col] || null

                          if (!det) {
                            return (
                              <div
                                key={`${r}-${col}`}
                                className="seatmap-cell seatmap-empty"
                              />
                            )
                          }

                          const state = getSeatState(det)
                          const selectedNow = isSelected(det)

                          const seatClass =
                            state !== 'available'
                              ? 'seat-unavailable'
                              : selectedNow
                              ? 'seat-selected'
                              : 'seat-available'

                          const title = `${seatCode(det)} · ${
                            state === 'available' ? 'Disponible' : 'Ocupado'
                          }`

                          return (
                            <button
                              key={det.id_event_zone_detail}
                              type="button"
                              className={`seatmap-cell seat-btn ${seatClass}`}
                              onClick={_ => toggleSeat(det)}
                              disabled={state !== 'available'}
                              title={title}
                            >
                              <span className="seat-label">
                                {seatCode(det)}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-center text-muted small mt-2">
                  Tip: click para seleccionar o deseleccionar.
                </div>
              </div>
            </div>

            {/* FILA 2: DISPONIBILIDAD (COMPACTA) */}
            <div className="zoneModalRow zoneModalRow--legend">
              <div className="zoneLegendBar">
                <LegendDot label="Disponible" className="seat-available" />
                <LegendDot label="Ocupado" className="seat-unavailable" />
                <LegendDot label="Seleccionado" className="seat-selected" />
              </div>
            </div>

            {/* FILA 3: SELECCIONADOS + BOTONES (STICKY) */}
            <div className="zoneModalRow zoneModalRow--footer">
              <div className="zoneFooterCard">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Tu selección</span>
                  <Badge bg={selected.length ? 'primary' : 'secondary'}>
                    {selected.length}
                  </Badge>
                </div>

                {selected.length ? (
                  <>
                    <div className="zoneSelectedWrap mb-2">
                      {selected
                        .slice()
                        .sort((a, b) => a.code.localeCompare(b.code))
                        .map(s => (
                          <button
                            key={s.id}
                            type="button"
                            className="zoneSelectedPill"
                            title="Quitar"
                            onClick={_ =>
                              setSelected(prev =>
                                prev.filter(
                                  x =>
                                    parseInt(x.id, 10) !==
                                    parseInt(s.id, 10)
                                )
                              )
                            }
                          >
                            <i className="fas fa-chair me-1" />
                            {s.code}
                            <span className="ms-2 text-danger">×</span>
                          </button>
                        ))}
                    </div>

                    <div className="d-flex justify-content-between small text-muted">
                      <span>Precio unitario</span>
                      <span>
                        {getRow?.name_money} {getRow?.price_event_zone}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between fw-semibold mt-1">
                      <span>Total</span>
                      <span>
                        {getRow?.name_money} {total}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-muted small">
                    Aún no has seleccionado ningún {zoneTypeLabel}. Elige desde
                    el mapa.
                  </div>
                )}

                <div className="d-grid gap-2 mt-3">
                  <Button
                    size="lg"
                    type="submit"
                    disabled={!canConfirm}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <i className="fas fa-check-circle me-2" />
                    Confirmar
                  </Button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={!selected.length}
                    onClick={_ => setSelected([])}
                  >
                    <i className="fas fa-eraser me-2" />
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ESTILOS (MODAL FRIENDLY) */}
        <style>{`
          /* Layout 3 filas */
          .zoneModalLayout{
            display:flex;
            flex-direction:column;
            gap:10px;
          }
          .zoneModalRow--map{
            /* mapa ocupa el espacio disponible */
          }
          .zoneModalRow--legend{
            margin-top:-2px;
          }
          .zoneModalRow--footer{
            position: sticky;
            bottom: 0;
            z-index: 3;
          }

          .zoneMapCard{
            border:1px solid #e9ecef;
            background:#fff;
            border-radius:14px;
            padding:10px;
          }
          .zoneMapHeader{
            display:flex;
            justify-content:center;
            margin-bottom:6px;
          }

          /* Leyenda compacta */
          .zoneLegendBar{
            border:1px solid #e9ecef;
            background:#fff;
            border-radius:14px;
            padding:8px 10px;
            display:flex;
            justify-content:center;
            gap:14px;
            flex-wrap:wrap;
          }
          .legend-dot{
            width:10px;height:10px;border-radius:999px;display:inline-block;
            border:1px solid rgba(0,0,0,.06);
          }
          .legend-item{
            display:inline-flex;
            align-items:center;
            gap:8px;
            font-size:12px;
            color:#6c757d;
            white-space:nowrap;
          }

          /* Footer sticky card */
          .zoneFooterCard{
            border:1px solid #e9ecef;
            background:#fff;
            border-radius:14px;
            padding:10px;
            box-shadow: 0 -10px 25px rgba(0,0,0,.04);
          }

          .zoneSelectedWrap{
            display:flex;
            flex-wrap:wrap;
            gap:8px;
            max-height: 84px;
            overflow:auto;
            padding-right: 4px;
          }
          .zoneSelectedPill{
            border:1px solid #e9ecef;
            background:#f8f9fa;
            color:#212529;
            border-radius:999px;
            padding:6px 10px;
            font-size:12px;
            cursor:pointer;
            transition: transform .05s ease, box-shadow .12s ease;
          }
          .zoneSelectedPill:hover{
            box-shadow:0 8px 18px rgba(0,0,0,.06);
          }
          .zoneSelectedPill:active{
            transform: translateY(1px);
          }

          /* Seatmap scroll (clave en modal) */
          .seatmap-scroll{
            max-height: 45vh; /* ajusta según tu modal */
            overflow:auto;
            padding-bottom:6px;
          }
          .seatmap-grid{
            min-width:max-content;
          }
          .seatmap-row{
            display:grid;
            grid-auto-flow:column;
            gap:6px;
            margin-bottom:6px;
            align-items:center;
          }
          .seatmap-cell{
            width:36px;
            height:34px;
            display:flex;
            align-items:center;
            justify-content:center;
            border-radius:10px;
            border:1px solid #e9ecef;
            background:#fff;
            font-size:11px;
            user-select:none;
          }
          .seatmap-corner,
          .seatmap-colhead,
          .seatmap-rowhead{
            background:#f8f9fa;
            border-color:#eef0f2;
            font-weight:700;
            color:#495057;
          }
          .seatmap-empty{
            background:transparent;
            border-color:transparent;
          }
          .seat-btn{
            transition: transform .05s ease, box-shadow .12s ease, opacity .12s ease;
          }
          .seat-btn:active{
            transform: translateY(1px);
          }
          .seat-btn:focus{
            outline:none;
            box-shadow:0 0 0 .2rem rgba(13,110,253,.15);
          }

          /* Estados */
          .seat-available{
            background:#e7f5ff;
            border-color:#a5d8ff;
            color:#0b4b7a;
          }
          .seat-available:hover{
            box-shadow:0 6px 16px rgba(0,0,0,.08);
          }
          .seat-unavailable{
            background:#f1f3f5;
            border-color:#dee2e6;
            color:#868e96;
            opacity:.85;
            cursor:not-allowed;
          }
          .seat-selected{
            background:#d3f9d8;
            border-color:#8ce99a;
            color:#1b4332;
            box-shadow:0 8px 18px rgba(0,0,0,.10);
          }
          .seat-label{
            font-weight:800;
            letter-spacing:.2px;
          }

          /* En pantallas muy chicas, reduce un poco */
          @media (max-width: 420px){
            .seatmap-cell{ width:34px; height:32px; font-size:10px; }
            .seatmap-row{ gap:5px; margin-bottom:5px; }
            .seatmap-scroll{ max-height: 42vh; }
          }
        `}</style>
      </ReactPlaceholder>

      {doSubmit && <LoadingIcon />}
      <SweetAlert2 {...swalProps} />
    </FormCustom>
  )
}

function LegendDot({ label, className }) {
  return (
    <span className="legend-item">
      <span className={`legend-dot ${className}`} />
      {label}
    </span>
  )
}
